import { useEffect, useRef, useState, FormEvent } from 'react';
import Button from '@components/Button';
import {
  listAffirmationTypes,
  createAffirmationType,
  updateAffirmationType,
  deleteAffirmationType,
  type NewAffirmationType,
} from '@lib/affirmations';
import type { AffirmationType } from '@lib/affirmations';

type AffirmationFormState = {
  name: string;
  audio_url: string;
  active: boolean;
};

const blankForm: AffirmationFormState = {
  name: '',
  audio_url: '',
  active: true,
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function ActionsMenu(props: {
  id?: string;
  label?: string;
  side?: 'top' | 'bottom';
  items: Array<{
    key: string;
    label: string;
    onClick: () => void;
    className?: string;
    disabled?: boolean;
  }>;
}) {
  const { label = 'Actions', items, side = 'bottom' } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [ref]);

  return (
    <div ref={ref} className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <span className="sr-only">{label}</span>
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      </button>

      {open ? (
        <div
          className={`absolute right-0 z-50 w-56 rounded-2xl border border-gray-100 bg-white shadow-lg ${
            side === 'top'
              ? 'bottom-full mb-2 origin-bottom-right'
              : 'top-full mt-2 origin-top-right'
          }`}
        >
          <div className="py-2">
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  it.onClick();
                  setOpen(false);
                }}
                disabled={it.disabled}
                className={`w-full px-4 py-2 text-left text-sm ${it.className ?? 'text-gray-700'} ${it.disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'}`}
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AdminAffirmations() {
  const [affirmationTypes, setAffirmationTypes] = useState<AffirmationType[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<AffirmationType | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState<AffirmationType | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formData, setFormData] = useState<AffirmationFormState>(blankForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listAffirmationTypes();
        if (isMounted) setAffirmationTypes(data);
      } catch (err) {
        if (isMounted) {
          setError(
            err instanceof Error
              ? err.message
              : String(err ?? 'Unable to load affirmation types')
          );
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const openAdd = () => {
    setEditing(null);
    setFormData(blankForm);
    setFormError(null);
    setIsModalOpen(true);
  };

  const openEdit = (affirmation: AffirmationType) => {
    setEditing(affirmation);
    setFormError(null);
    setFormData({
      name: affirmation.name,
      audio_url: affirmation.audio_url,
      active: affirmation.active,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    setEditing(null);
  };

  const openDelete = (affirmation: AffirmationType) => {
    setDeleting(affirmation);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const closeDelete = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setDeleting(null);
    setDeleteError(null);
  };

  const handleInputChange = (
    field: keyof AffirmationFormState,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);

    const payload: NewAffirmationType = {
      name: formData.name.trim(),
      audio_url: formData.audio_url.trim(),
      active: formData.active,
    };

    try {
      if (!payload.name) throw new Error('Name is required');
      if (!payload.audio_url) throw new Error('Audio URL is required');
      if (editing) {
        const updated = await updateAffirmationType(editing.id, payload);
        setAffirmationTypes((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item))
        );
      } else {
        const created = await createAffirmationType(payload);
        setAffirmationTypes((prev) => [created, ...prev]);
      }
      setIsModalOpen(false);
      setEditing(null);
      setFormData(blankForm);
    } catch (err) {
      setFormError(
        err instanceof Error
          ? err.message
          : String(err ?? 'Failed to save affirmation type')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteAffirmationType(deleting.id);
      setAffirmationTypes((prev) => prev.filter((b) => b.id !== deleting.id));
      setShowDeleteModal(false);
      setDeleting(null);
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : String(err ?? 'Failed to delete')
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 py-12">
        <div className="mb-6 text-center">
          <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
            Affirmation Types
          </h1>
          <p className="text-lg text-gray-600">
            Manage different categories of affirmations and their audio tracks.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <div>
              <span className="font-semibold text-gray-900">
                {affirmationTypes.length}
              </span>{' '}
              total
            </div>
          </div>
        </div>

        <div className="animate-fade-in rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div />
            <Button
              type="button"
              onClick={openAdd}
              className="item-selected inline-flex items-center rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Add Affirmation Type
            </Button>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          <div className="mt-6">
            {loading ? (
              <div className="space-y-4">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="h-24 animate-pulse rounded-2xl bg-white/60 backdrop-blur"
                  />
                ))}
              </div>
            ) : affirmationTypes.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
                No affirmation types yet. Add one to get started.
              </div>
            ) : (
              <>
                <div className="space-y-4 divide-y divide-white/40 lg:hidden">
                  {affirmationTypes.map((affirmation, index) => (
                    <div
                      key={affirmation.id}
                      className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
                    >
                      <div className="flex flex-col gap-4">
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">
                            {affirmation.name}
                          </h2>
                          <div className="mt-1 truncate text-xs text-gray-500">
                            {affirmation.audio_url}
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${affirmation.active ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}
                            >
                              {affirmation.active ? 'Active' : 'Not active'}
                            </span>
                            <div className="text-xs uppercase tracking-wide text-gray-500">
                              Created {formatDate(affirmation.created_at)}
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <ActionsMenu
                            id={affirmation.id}
                            side={
                              index > 0 && index === affirmationTypes.length - 1
                                ? 'top'
                                : 'bottom'
                            }
                            items={[
                              {
                                key: 'edit',
                                label: 'Edit',
                                onClick: () => openEdit(affirmation),
                                className: '!text-indigo-700',
                              },
                              {
                                key: 'delete',
                                label: 'Delete',
                                onClick: () => openDelete(affirmation),
                                className: '!text-rose-700',
                              },
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="hidden lg:block">
                  <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                    <table className="min-w-full text-left text-sm text-gray-800">
                      <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-4 py-3">Name</th>
                          <th className="px-4 py-3">Audio URL</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Created</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {affirmationTypes.map((affirmation, index) => (
                          <tr
                            key={affirmation.id}
                            className="border-t border-gray-100 hover:bg-indigo-50"
                          >
                            <td className="px-4 py-4 align-top font-semibold text-gray-900">
                              {affirmation.name}
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              <div className="max-w-xs truncate text-xs text-gray-500">
                                {affirmation.audio_url}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top text-gray-900">
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${affirmation.active ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
                              >
                                {affirmation.active ? 'Active' : 'Not active'}
                              </span>
                            </td>
                            <td className="px-4 py-4 align-top text-xs text-gray-900">
                              {formatDate(affirmation.created_at)}
                            </td>
                            <td className="px-4 py-4 text-right">
                              <ActionsMenu
                                id={affirmation.id}
                                side={
                                  index > 0 &&
                                  index >= affirmationTypes.length - 2
                                    ? 'top'
                                    : 'bottom'
                                }
                                items={[
                                  {
                                    key: 'edit',
                                    label: 'Edit',
                                    onClick: () => openEdit(affirmation),
                                    className: '!text-indigo-700',
                                  },
                                  {
                                    key: 'delete',
                                    label: 'Delete',
                                    onClick: () => openDelete(affirmation),
                                    className: '!text-rose-700',
                                  },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative flex max-h-[90vh] w-full max-w-xl flex-col rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeModal}
              className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close affirmation modal"
              disabled={saving}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">
              {editing ? 'Edit Affirmation Type' : 'Add Affirmation Type'}
            </h2>
            <p className="text-sm text-gray-500">
              {editing
                ? 'Update this category of affirmations.'
                : 'Add a new category of affirmations.'}
            </p>
            {formError && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {formError}
              </div>
            )}
            <form
              className="mt-6 flex min-h-0 flex-1 flex-col"
              onSubmit={handleSave}
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-2">
                <div>
                  <label
                    htmlFor="affirmation-name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Name
                  </label>
                  <input
                    id="affirmation-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    placeholder="e.g. Panic Attack Relief"
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="affirmation-audio-url"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Audio URL (.mp3)
                  </label>
                  <input
                    id="affirmation-audio-url"
                    type="text"
                    value={formData.audio_url}
                    onChange={(e) =>
                      handleInputChange('audio_url', e.target.value)
                    }
                    required
                    placeholder="https://example.com/audio.mp3"
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <label className="flex items-center gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) =>
                      handleInputChange('active', e.target.checked)
                    }
                    className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Active (visible to users)
                </label>
              </div>
              <div className="flex flex-shrink-0 justify-end gap-3 pt-6">
                <button
                  type="button"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="item-selected rounded-full bg-indigo-600 px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {saving
                    ? 'Saving...'
                    : editing
                      ? 'Save Changes'
                      : 'Add Affirmation Type'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && deleting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeDelete}
              className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close delete affirmation modal"
              disabled={isDeleting}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">
              Delete Affirmation Type
            </h2>
            <p className="text-sm text-gray-500">
              Are you sure you want to remove "{deleting.name}"? This action
              cannot be undone.
            </p>
            {deleteError && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {deleteError}
              </div>
            )}
            <div className="mt-6 flex items-center justify-end gap-3">
              <Button type="button" onClick={closeDelete}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAffirmations;
